import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import useBackgroundImage from '../hooks/useBackgroundImage';
import { getFullScreenBackgroundStyle } from '../utils/backgroundStyles';

const TermsOfService = () => {
  const navigate = useNavigate();
  const backgroundImage = useBackgroundImage();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main 
      className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-32"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 mb-6 text-gray-700 hover:text-gray-900 transition-colors duration-200 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
        >
          <FaArrowLeft className="w-4 h-4" />
          <span className="poppins font-medium">Back</span>
        </button>

        {/* Terms of Service Content */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 p-6 md:p-10 lg:p-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 poppins mb-4">
            Terms of Service
          </h1>
          
          <p className="text-gray-600 poppins mb-8">
            <strong>Last Updated:</strong> October 15, 2025
          </p>

          <div className="space-y-8 text-gray-700 poppins leading-relaxed">
            <p>
              Welcome to Yorokart, a product of Yoro Technologies ("we", "our", or "us").
              These Terms of Service ("Terms") govern your access to and use of the Yorokart website and services. By accessing or using Yorokart, you agree to these Terms.
              If you do not agree, please do not use the platform.
            </p>

            <hr className="border-gray-300" />

            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. About Yorokart</h2>
              <p className="mb-3">
                Yorokart is a digital platform that allows businesses such as restaurants, mobile stores, grocery shops, dessert shops, and real estate companies to display their products or menus online.
                Customers can browse products and send orders or inquiries directly to the vendors via WhatsApp.
              </p>
              <p className="font-semibold">
                Yorokart acts only as a technology service provider — we do not sell, deliver, or handle any payments between customers and vendors.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use of the Platform</h2>
              <p className="mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use Yorokart only for lawful purposes.</li>
                <li>Not misuse the website (e.g., attempt to hack, spam, or disrupt service).</li>
                <li>Provide accurate information when placing an order or inquiry.</li>
              </ul>
              <p className="mt-3">
                We reserve the right to suspend or terminate access for users who violate these Terms or misuse the platform.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Vendor and Customer Relationship</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Yorokart connects customers directly to vendors through WhatsApp.</li>
                <li>All communication, negotiation, and transaction details occur between the customer and the vendor.</li>
                <li>Yoro Technologies is not responsible for:
                  <ul className="list-circle list-inside space-y-1 ml-6 mt-2">
                    <li>Product quality or pricing</li>
                    <li>Availability or delivery of items</li>
                    <li>Payment handling or disputes between customer and vendor</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-3 font-semibold">
                The vendor is solely responsible for fulfilling any order or service commitment.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Orders and WhatsApp Integration</h2>
              <p className="mb-3">
                When you click "Place Order," Yorokart redirects you to WhatsApp with a pre-filled message containing your order details.
              </p>
              <p className="mb-2">Please note:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The order is sent directly to the vendor's WhatsApp account.</li>
                <li>Yorokart does not store your messages or order data once you leave our website.</li>
                <li>Communication is governed by WhatsApp's Terms of Service and Privacy Policy.</li>
              </ul>
            </section>

            <hr className="border-gray-300" />

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Content and Accuracy</h2>
              <p className="mb-2">While we aim to display accurate information, Yorokart does not guarantee that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Product details, prices, or images are always correct or up to date.</li>
                <li>Vendor information is verified or error-free.</li>
              </ul>
              <p className="mt-3">
                All product data is provided by the respective vendors. Users are encouraged to confirm details directly before placing an order.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="mb-3">
                All content on Yorokart, including the name, logo, design, and technology, is the intellectual property of Yoro Technologies and may not be copied, modified, or redistributed without permission.
              </p>
              <p>
                Vendor logos, product names, and trademarks belong to their respective owners.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="mb-2">To the maximum extent permitted by law:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Yoro Technologies shall not be liable for any loss, damage, or expense arising from your use of Yorokart or any transaction between you and a vendor.</li>
                <li>Yorokart is provided "as is" without any warranties, express or implied.</li>
              </ul>
            </section>

            <hr className="border-gray-300" />

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p>
                Our platform may include links or redirections to third-party services such as WhatsApp or vendors' websites.
                We are not responsible for the content, policies, or actions of those third parties.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
              <p className="mb-2">We may suspend or terminate your access to Yorokart without notice if:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You violate these Terms</li>
                <li>Your actions cause harm or risk to the platform, users, or vendors</li>
              </ul>
              <p className="mt-3">
                Upon termination, your right to use the platform immediately ceases.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to the Terms</h2>
              <p>
                We may update these Terms periodically. Updated versions will be posted on our website with the revised "Last Updated" date. Continued use of the platform means you accept the updated Terms.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="mb-3">If you have any questions or concerns about these Terms, please contact:</p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900 text-lg mb-3">Yoro Technologies</p>
                <p className="mb-2"><strong>Email:</strong> <a href="mailto:info@yorotechnologies.com" className="text-blue-600 hover:text-blue-800 underline">info@yorotechnologies.com</a></p>
                <p className="mb-2"><strong>Website:</strong> <a href="https://www.yorotechnologies.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">www.yorotechnologies.com</a></p>
                <p><strong>Location:</strong> Calicut, Kerala, India</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TermsOfService;

